import React, { useEffect, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, ZoomControl } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Header } from "../components";
import { useStateContext } from "../contexts/ContextProvider";

// Override default icons which can sometimes fail to load
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Custom icon for student markers
const studentIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
  shadowSize: [41, 41],
  className: 'student-marker' // Add custom class for styling
});

const LocationMap = () => {
  const { currentColor } = useStateContext();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchLocations = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          "https://apissc.sainikschoolcadet.com/api/student-location/all-with-students",
          { signal: controller.signal }
        );
        setLocations(response.data);
        setError(null);
      } catch (err) {
        if (axios.isCancel(err) || err.name === 'CanceledError' || err.name === 'AbortError') {
          return; // Request was aborted, ignore
        }
        console.error("Error fetching student location data:", err);
        setError("Failed to load student locations. Please try again later.");
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };
    
    fetchLocations();
    
    // Set up periodic refresh
    const refreshInterval = setInterval(fetchLocations, 300000); // Refresh every 5 minutes
    
    return () => {
      controller.abort();
      clearInterval(refreshInterval);
    };
  }, []);

  // Default center of India
  const defaultCenter = [21.1458, 79.0882];
  
  // Calculate map center based on available locations or use default
  const mapCenter = locations.length > 0 
    ? [parseFloat(locations[0].latitude), parseFloat(locations[0].longitude)]
    : defaultCenter;

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl shadow-md">
      
      {loading ? (
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2" style={{ borderColor: currentColor }}></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      ) : locations.length === 0 ? (
        <div className="flex justify-center items-center h-96 bg-gray-50 rounded-lg">
          <div className="text-center p-5">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No student locations available</h3>
            <p className="mt-1 text-sm text-gray-500">No students have shared their location data yet.</p>
          </div>
        </div>
      ) : (
        <div className="dashboard-map border border-gray-200 rounded-lg overflow-hidden shadow-lg">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Student Locations ({locations.length})</h3>
              <span className="text-sm text-gray-500">Last updated: {new Date().toLocaleTimeString()}</span>
            </div>
          </div>
          
          <MapContainer
            center={mapCenter}
            zoom={5}
            style={{ height: "700px", width: "100%" }}
            zoomControl={false}
          >
            <ZoomControl position="bottomright" />
            <TileLayer 
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' 
            />

            <MarkerClusterGroup 
              chunkedLoading
              iconCreateFunction={(cluster) => {
                return L.divIcon({
                  html: `<div style="background-color: ${currentColor}; color: white; border-radius: 50%; width: 30px; height: 30px; display: flex; justify-content: center; align-items: center; font-weight: bold;">${cluster.getChildCount()}</div>`,
                  className: 'custom-marker-cluster',
                  iconSize: L.point(30, 30)
                });
              }}
            >
              {locations.map((location) => (
                <Marker
                  key={location.location_id}
                  position={[parseFloat(location.latitude), parseFloat(location.longitude)]}
                  icon={studentIcon}
                >
                  <Popup className="custom-popup">
                    <div className="student-popup">
                      <div className="popup-header" style={{ backgroundColor: currentColor, color: 'white', padding: '8px', borderRadius: '4px 4px 0 0', marginBottom: '8px' }}>
                        <h3 className="font-bold">Student Information</h3>
                      </div>
                      
                      <div className="popup-content">
                        <p><strong>Name:</strong> {location.Student?.f_name || 'Unknown'}</p>
                        <p><strong>Student ID:</strong> {location.s_id}</p>
                        <p><strong>School:</strong> {location.Student?.c_school || 'Not specified'}</p>
                        <p><strong>Class:</strong> {location.Student?.c_entry || 'Not specified'}</p>
                        <p><strong>Last updated:</strong> {new Date(location.updated_at).toLocaleString()}</p>
                      </div>
                      
                      <div className="popup-footer mt-2 pt-2 border-t border-gray-200">
                        <a
                          href={`/view-student/${location.s_id}`}
                          className="text-white py-1 px-3 rounded text-sm font-medium"
                          style={{ backgroundColor: currentColor, display: 'inline-block', textDecoration: 'none' }}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Student Profile
                        </a>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          </MapContainer>
          
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              This map shows the last known location of students who have the app installed and location sharing enabled.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationMap;